import { useState, useEffect } from 'react'
import { PageLayout } from '@/components/layout'
import { MemberList, Member } from '@/components/organization/MemberList'
import { InviteMemberModal } from '@/components/organization/InviteMemberModal'
import { Card, Button, Input, Badge, LoadingSpinner } from '@/components/common'
import { useAuth } from '@/hooks/useAuth'
import { useOrganization, useMembers, useInviteMember, useUpdateOrganization, useRemoveMember } from '@/hooks/useOrganization'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { organizationService } from '@/services/organization'
import { useToast } from '@/contexts/ToastContext'

export const OrganizationSettings = () => {
  const { user } = useAuth()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  
  const organizationId = user?.organizationId || ''
  const { data: organization, isLoading: orgLoading, error: orgError } = useOrganization(organizationId)
  const { data: membersData, isLoading: membersLoading } = useMembers(organizationId)
  
  const [orgName, setOrgName] = useState(organization?.name || '')
  const [orgDescription, setOrgDescription] = useState(organization?.description || '')
  
  // Update local state when organization data loads
  useEffect(() => {
    if (organization) {
      setOrgName(organization.name)
      setOrgDescription(organization.description || '')
    }
  }, [organization])

  // Transform API members data to component format
  const members: Member[] = membersData?.members.map((m) => ({
    id: m.member_id,
    name: m.full_name,
    email: m.email,
    role: m.role,
    joinedAt: m.joined_at,
    isActive: m.is_active,
  })) || []

  const updateOrgMutation = useUpdateOrganization(organizationId)
  const inviteMutation = useInviteMember(organizationId)
  const removeMutation = useRemoveMember(organizationId)

  // Create a reusable mutation for updating member roles
  const updateRoleMutation = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: 'owner' | 'admin' | 'member' }) =>
      organizationService.updateMemberRole(organizationId, memberId, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', organizationId] })
    },
  })

  const isLoading = orgLoading || membersLoading

  const handleInvite = async (data: { email: string; role: 'admin' | 'member' }) => {
    inviteMutation.mutate(
      { email: data.email, role: data.role },
      {
        onSuccess: () => {
          setShowInviteModal(false)
          showToast('Member invited successfully', 'success')
          queryClient.invalidateQueries({ queryKey: ['members', organizationId] })
        },
        onError: (error: any) => {
          showToast(
            error?.response?.data?.detail || 'Failed to invite member',
            'error'
          )
        },
      }
    )
  }

  const handleRemove = (memberId: string) => {
    removeMutation.mutate(memberId, {
      onSuccess: () => {
        showToast('Member removed successfully', 'success')
        queryClient.invalidateQueries({ queryKey: ['members', organizationId] })
      },
      onError: (error: any) => {
        showToast(
          error?.response?.data?.detail || 'Failed to remove member',
          'error'
        )
      },
    })
  }

  const handleUpdateRole = (memberId: string, newRole: 'owner' | 'admin' | 'member') => {
    updateRoleMutation.mutate(
      { memberId, role: newRole },
      {
        onSuccess: () => {
          showToast('Member role updated successfully', 'success')
          queryClient.invalidateQueries({ queryKey: ['members', organizationId] })
        },
        onError: (error: any) => {
          showToast(
            error?.response?.data?.detail || 'Failed to update member role',
            'error'
          )
        },
      }
    )
  }

  const handleSaveOrganization = () => {
    updateOrgMutation.mutate(
      { name: orgName, description: orgDescription },
      {
        onSuccess: () => {
          setIsEditing(false)
          showToast('Organization updated successfully', 'success')
          queryClient.invalidateQueries({ queryKey: ['organization', organizationId] })
        },
        onError: (error: any) => {
          showToast(
            error?.response?.data?.detail || 'Failed to update organization',
            'error'
          )
        },
      }
    )
  }

  if (isLoading) {
    return (
      <PageLayout showHeader={true}>
        <div className="p-6 md:p-8 lg:p-10 flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    )
  }

  if (orgError || !organization) {
    return (
      <PageLayout showHeader={true}>
        <div className="p-6 md:p-8 lg:p-10">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400">
                Error loading organization: {orgError instanceof Error ? orgError.message : 'Organization not found'}
              </p>
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout showHeader={true}>
      <div className="p-6 md:p-8 lg:p-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-main-light dark:text-text-main-dark mb-2">
              Organization Settings
            </h1>
            <p className="text-text-sub-light dark:text-text-sub-dark">
              Manage your organization details and team members
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Organization Details */}
            <div className="lg:col-span-1 space-y-6">
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-text-main-light dark:text-text-main-dark">
                    Organization Details
                  </h2>
                  {!isEditing && (
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                      Edit
                    </Button>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-4">
                    <Input
                      label="Organization Name"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      required
                    />
                    <div>
                      <label className="block text-sm font-medium text-text-main-light dark:text-text-main-dark mb-2">
                        Description
                      </label>
                      <textarea
                        value={orgDescription}
                        onChange={(e) => setOrgDescription(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-surface-light dark:bg-surface-dark text-text-main-light dark:text-text-main-dark focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="primary" size="sm" onClick={handleSaveOrganization}>
                        Save
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setIsEditing(false)
                          setOrgName(organization.name)
                          setOrgDescription(organization.description || '')
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">
                        Name
                      </p>
                      <p className="font-semibold text-text-main-light dark:text-text-main-dark">
                        {organization.name}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">
                        Description
                      </p>
                      <p className="text-text-main-light dark:text-text-main-dark">
                        {organization.description || 'No description'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">
                        Created
                      </p>
                      <p className="text-text-main-light dark:text-text-main-dark">
                        {new Date(organization.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">
                        Members
                      </p>
                      <Badge variant="default">{organization.member_count || members.length} members</Badge>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Right Column - Member List */}
            <div className="lg:col-span-2">
              <MemberList
                members={members}
                currentUserId={user?.id}
                onRemove={handleRemove}
                onUpdateRole={handleUpdateRole}
                onInvite={() => setShowInviteModal(true)}
              />
            </div>
          </div>
        </div>
      </div>

      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInvite}
      />
    </PageLayout>
  )
}

